import { CAST_AI } from '@cast/constants';
import {
  Button,
  Divider,
  Icons,
  MetadataChipV2,
  MetricsBox,
  Panel,
  Tooltip,
  tokens,
  Typography,
  Stack,
} from '@cast/design-system';
import { CLUSTER_DASHBOARD_TOOLTIPS } from '@cast/features-cluster-shared';
import { dashesOrNum, formatCurrency, getCurrencyPrecision } from '@cast/utils';
import { useAbility } from '@cast/shared-ability-data-access';
import {
  isKOPSCluster,
  useCluster,
} from '@cast/shared-cluster-context-data-access';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';

import { PERFORMANCE_BANNER_COPY } from '../_constants/performanceBanner';
import { clusterDashboardChipSx } from '../_constants/dashboardTokens';
import { useInstallClusterAgentDialog } from '../_hooks/useInstallClusterAgentDialog';
import { usePerformanceBannerMetrics } from '../_hooks/usePerformanceBannerMetrics';
import { AgentTimeline } from './shared/AgentTimeline';
import { BannerMetricPanelSkeleton } from './shared/BannerMetricPanelSkeleton';
import { BannerMetricTrendChip } from './shared/BannerMetricTrendChip';
import { ClusterBannerLayout } from './shared/ClusterBannerLayout';

const METRIC_PANEL_MIN_WIDTH = 184;

const unlockIconMap = {
  Eye: Icons.Eye,
  CrosshairSimple: Icons.CrosshairSimple,
  CurrencyDollarSimple: Icons.CurrencyDollarSimple,
} as const;

const timelineIconMap = {
  CloudArrowDown: Icons.CloudArrowDown,
  ChartBar: Icons.ChartBar,
  Sparkle: Icons.Sparkle,
} as const;

const formatPctValue = (value?: number) =>
  dashesOrNum({
    value,
    roundBy: 1,
    decimals: 1,
    mapDashes: () => '—',
  });

const formatCurrencyValue = (value?: number) =>
  value != null && value > 0
    ? formatCurrency(value, {
        maximumFractionDigits: getCurrencyPrecision(value),
      })
    : '—';

export const PerformanceBanner = () => {
  const { openInstallClusterAgentDialog } = useInstallClusterAgentDialog();
  const {
    cluster,
    canBeManaged,
    isTerraformManaged,
    clusterOnboardingPhase2,
    isKentEligible,
    isKentManaged,
  } = useCluster();
  const isDemoMode = useIsClusterDemoMode();
  const canEditCluster = useAbility().can('update', 'Clusters');
  const {
    cpuUtilizationPct,
    monthlySavings,
    savingsLabel,
    hasMetrics,
    isMetricsLoading,
  } = usePerformanceBannerMetrics();
  const hasPositiveMonthlySavings =
    monthlySavings != null && monthlySavings > 0;

  const copy = PERFORMANCE_BANNER_COPY;

  const isInstallBlocked =
    !canBeManaged ||
    isDemoMode ||
    isKOPSCluster(cluster) ||
    isKentEligible ||
    isKentManaged;

  const isInstallDisabled =
    isInstallBlocked ||
    !canEditCluster ||
    isTerraformManaged ||
    clusterOnboardingPhase2;

  return (
    <ClusterBannerLayout
      testId="performance-banner"
      main={
        <Stack flex={1} gap={16} p={24} minWidth={0}>
          <Stack gap={24}>
            <Stack gap={8}>
              <Typography
                variant="L12B"
                sx={{
                  color: tokens.theme.fg.structural.secondary,
                  fontWeight: 600,
                }}
              >
                {copy.label}
              </Typography>
              <Stack gap={4}>
                <Typography
                  variant="h5"
                  sx={{ color: tokens.theme.fg.structural.primary }}
                >
                  {copy.title}
                </Typography>
                <Typography
                  variant="P12R"
                  sx={{ color: tokens.theme.fg.structural.secondary }}
                >
                  {copy.descriptionPrefix}{' '}
                  <Typography
                    component="span"
                    variant="P12M"
                    sx={{ color: tokens.theme.fg.status.success }}
                  >
                    {savingsLabel}/month
                  </Typography>{' '}
                  {copy.descriptionSuffix}
                </Typography>
              </Stack>
            </Stack>

            {isMetricsLoading ? (
              <Stack direction="row" gap={10} flexWrap="wrap">
                <BannerMetricPanelSkeleton minWidth={METRIC_PANEL_MIN_WIDTH} />
                <BannerMetricPanelSkeleton minWidth={METRIC_PANEL_MIN_WIDTH} />
              </Stack>
            ) : hasMetrics ? (
              <Stack direction="row" gap={10} flexWrap="wrap">
                <Panel
                  sx={{
                    p: 16,
                    flex: `1 1 ${METRIC_PANEL_MIN_WIDTH}px`,
                    minWidth: METRIC_PANEL_MIN_WIDTH,
                  }}
                >
                  <MetricsBox
                    size="m"
                    slots={{
                      label: 'CPU utilization',
                      value: formatPctValue(cpuUtilizationPct),
                      unit: cpuUtilizationPct != null ? '%' : undefined,
                      footer:
                        cpuUtilizationPct != null ? (
                          <BannerMetricTrendChip
                            direction="up"
                            label={`${formatPctValue(cpuUtilizationPct)}%`}
                          />
                        ) : undefined,
                    }}
                  />
                </Panel>
                <Panel
                  sx={{
                    p: 16,
                    flex: `1 1 ${METRIC_PANEL_MIN_WIDTH}px`,
                    minWidth: METRIC_PANEL_MIN_WIDTH,
                  }}
                >
                  <MetricsBox
                    size="m"
                    slots={{
                      label: 'Compute cost',
                      value: hasPositiveMonthlySavings ? (
                        <>
                          <Typography
                            component="span"
                            variant="L12R"
                            color={tokens.theme.fg.structural.secondary}
                            sx={{ mr: 4 }}
                          >
                            up to
                          </Typography>
                          {formatCurrencyValue(monthlySavings)}
                        </>
                      ) : (
                        formatCurrencyValue(monthlySavings)
                      ),
                      footer: hasPositiveMonthlySavings ? (
                        <BannerMetricTrendChip
                          direction="down"
                          label={formatCurrencyValue(monthlySavings)}
                        />
                      ) : undefined,
                    }}
                  />
                </Panel>
              </Stack>
            ) : null}

            <Stack gap={4}>
              <Typography
                variant="P12M"
                sx={{ color: tokens.theme.fg.structural.secondary }}
              >
                {copy.installPrefix}
              </Typography>
              <Stack direction="row" gap={8} flexWrap="wrap">
                {copy.unlockFeatures.map((feature) => {
                  const Icon = unlockIconMap[feature.icon];
                  return (
                    <MetadataChipV2
                      key={feature.label}
                      label={feature.label}
                      size="m"
                      variant="subtle"
                      slots={{ startIcon: <Icon size={12} /> }}
                      sx={clusterDashboardChipSx.metadataLabel}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Stack>

          <Divider />

          <Tooltip
            title={CLUSTER_DASHBOARD_TOOLTIPS.actionRequiresTerraform}
            disableHoverListener={!isTerraformManaged}
            wrapIf={isTerraformManaged}
          >
            <span>
              <Button
                variant="primary"
                size="md"
                onClick={openInstallClusterAgentDialog}
                disabled={isInstallDisabled}
                testId="install-cluster-agent-button"
              >
                {clusterOnboardingPhase2
                  ? 'Enabling agent...'
                  : copy.cta(CAST_AI)}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      }
      sidePanel={
        <AgentTimeline
          label={copy.timelineLabel}
          steps={copy.timeline.map((step) => ({
            ...step,
            icon: (() => {
              const Icon = timelineIconMap[step.icon];
              return <Icon size={16} />;
            })(),
          }))}
        />
      }
    />
  );
};
